(function () {
  if (customElements.get('collection-image-carousel')) return;
  class CollectionImageCarousel extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.track = this.querySelector('[data-images-track]');
      this.previous = this.querySelector('[data-images-prev]');
      this.next = this.querySelector('[data-images-next]');
      this.counter = this.querySelector('[data-images-counter]');
      const signal = this.controller.signal;
      this.setupAutoplay(signal);
      this.previous.addEventListener('click', () => this.move(-1), { signal });
      this.next.addEventListener('click', () => this.move(1), { signal });
      this.track.addEventListener('scroll', () => this.update(), { signal, passive: true });
      this.track.addEventListener('keydown', (event) => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home' || event.key === 'End') this.track.scrollTo({ left: event.key === 'Home' ? 0 : this.track.scrollWidth, behavior: this.motion() });
        else this.move(event.key === 'ArrowRight' ? 1 : -1);
      }, { signal });
      this.resize = new ResizeObserver(() => this.update());
      this.resize.observe(this.track);
      this.load(signal);
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.resize?.disconnect();
      this.intersection?.disconnect();
      clearTimeout(this.autoTimer);
      this.controller = null;
    }
    async load(signal) {
      // Start from the bare collection URL: filters, pagination and grid sort must
      // not change which five collection products are featured here.
      const url = new URL(this.dataset.collectionUrl, location.origin);
      url.searchParams.set('section_id', 'collection-featured-images');
      url.searchParams.set('sort_by', 'price-descending');
      try {
        const response = await fetch(url, { signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Collection image request failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const slides = html.querySelectorAll('[data-collection-featured-slides] .collection-images__slide');
        if (!slides.length) throw new Error('No collection images');
        this.track.replaceChildren(...slides);
        this.hidden = false;
        this.setAttribute('aria-busy', 'false');
        this.querySelector('[data-images-controls]').hidden = slides.length < 2;
        this.update();
      } catch (error) {
        if (error.name !== 'AbortError') this.hidden = true;
      }
    }
    motion() { return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'; }
    setupAutoplay(signal) {
      this.autoEnabled = this.dataset.autoplay === 'true';
      this.autoInterval = Math.max(2, Math.min(10, Number(this.dataset.autoplayInterval) || 5)) * 1000;
      this.autoButton = this.querySelector('[data-images-autoplay]');
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = false;
      this.hovered = false;
      this.focused = false;
      this.interacting = false;
      this.inView = true;
      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') { this.hovered = true; this.syncAutoplay(); }
      }, { signal });
      this.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'mouse') { this.hovered = false; this.syncAutoplay(); }
      }, { signal });
      this.addEventListener('focusin', () => { this.focused = true; this.syncAutoplay(); }, { signal });
      this.addEventListener('focusout', (event) => {
        this.focused = this.contains(event.relatedTarget);
        this.syncAutoplay();
      }, { signal });
      this.addEventListener('pointerdown', () => { this.interacting = true; this.syncAutoplay(); }, { signal });
      const release = () => { this.interacting = false; this.syncAutoplay(); };
      window.addEventListener('pointerup', release, { signal });
      window.addEventListener('pointercancel', release, { signal });
      document.addEventListener('visibilitychange', () => this.syncAutoplay(), { signal });
      this.reducedMotion.addEventListener('change', () => this.syncAutoplay(), { signal });
      this.autoButton?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        // An explicit Play click is allowed to resume while this button has focus.
        if (!this.userPaused) this.focused = false;
        this.syncAutoplay();
      }, { signal });
      if (window.IntersectionObserver) {
        this.intersection = new IntersectionObserver((entries) => {
          this.inView = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.15;
          this.syncAutoplay();
        }, { threshold: 0.15 });
        this.intersection.observe(this);
      }
    }
    syncAutoplay() {
      clearTimeout(this.autoTimer);
      const loaded = this.getAttribute('aria-busy') === 'false';
      const scrollable = this.track.scrollWidth - this.track.clientWidth > 2;
      const enabled = this.autoEnabled && !this.reducedMotion.matches && loaded && scrollable;
      if (this.autoButton) {
        this.autoButton.hidden = !enabled;
        this.autoButton.textContent = this.userPaused ? 'Play' : 'Pause';
        this.autoButton.setAttribute('aria-label', this.userPaused ? 'Start automatic slideshow' : 'Pause automatic slideshow');
      }
      const playing = enabled && !this.userPaused && !this.hovered && !this.focused && !this.interacting && !document.hidden && this.inView;
      this.counter.setAttribute('aria-live', playing ? 'off' : 'polite');
      if (!playing || !this.isConnected) return;
      this.autoTimer = setTimeout(() => {
        const max = this.track.scrollWidth - this.track.clientWidth;
        if (max - this.track.scrollLeft < 2) this.track.scrollTo({ left: 0, behavior: this.motion() });
        else this.move(1);
        this.syncAutoplay();
      }, this.autoInterval);
    }
    move(direction) {
      const first = this.track.firstElementChild;
      if (!first) return;
      const step = first.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).gap || 0);
      this.track.scrollBy({ left: direction * step, behavior: this.motion() });
    }
    update() {
      if (this.getAttribute('aria-busy') === 'true' || !this.track.firstElementChild) return;
      const max = this.track.scrollWidth - this.track.clientWidth;
      const left = Math.max(0, this.track.scrollLeft);
      const step = this.track.firstElementChild.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).gap || 0);
      const visible = Math.max(1, Math.round((this.track.clientWidth + 16) / step));
      const start = Math.min(this.track.children.length, Math.round(left / step) + 1);
      const end = Math.min(this.track.children.length, start + visible - 1);
      this.previous.disabled = left < 2;
      this.next.disabled = max - left < 2;
      const label = (start === end ? start : start + '–' + end) + ' / ' + this.track.children.length;
      if (this.counter.textContent !== label) this.counter.textContent = label;
      this.syncAutoplay();
    }
  }
  customElements.define('collection-image-carousel', CollectionImageCarousel);
})();
